import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material'
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import {
  AdminPanelSettings,
  Settings,
  Close,
  AssignmentInd,
  RemoveRedEye,
} from '@mui/icons-material'
import { styled, useTheme } from '@mui/material/styles'
import BacktestEquityChart from './BacktestEquityChart'
import BacktestStatusIcon from './BacktestStatusIcon'
import { useApplicationSuiteContext } from '../../contexts/ApplicationSuiteProvider'
import { Tabs as AppTabs } from '../../layout/MainLayout'
import BacktestGradeIcon from './BacktestGradeIcon'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import ActionChip from '../actionChips/ActionChip'
import PatternVersionTitle from './PatternVersionTitle'
import FavoriteBacktestIconButton from './FavoriteBacktestIconButton'
import AssessmentIcon from '@mui/icons-material/Assessment'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import CreateAlertActionButton from '../../layout/settingsMenu/CreateAlertActionButton'
import RunAgentSessionButton from '../agent/RunAgentSessionButton'
import BacktestStatStrip from './BacktestStatStrip'
import BacktestRatioSelect from './BacktestRatioSelect'
import BacktestRatiosOverview from './BacktestRatiosOverview'
import {
  formatMonthYear,
  getTradeRatioStats,
  sortSummariesByProfit,
} from '../../common/utils/backtestSummaryStats'

type FavoriteActionType = {
  type: 'FAVORITE'
}

type ViewActionType = {
  type: 'VIEW'
  onView: (backtest: any) => void
}

type OpenSettingsActionType = {
  type: 'OPEN_SETTINGS'
  onSettingsClick: (isActive: boolean) => void
}

type SelectActionType = {
  type: 'SELECT'
  onSelect: (backtest: any) => void
}

type CreateAlertActionType = {
  type: 'CREATE_ALERT'
}

type RunAgentSessionActionType = {
  type: 'RUN_AGENT_SESSION'
}

type ViewSessionActionType = {
  type: 'VIEW_SESSION'
  onViewSession: () => void
}

type FooterActions =
  | FavoriteActionType
  | ViewActionType
  | OpenSettingsActionType
  | SelectActionType
  | CreateAlertActionType
  | RunAgentSessionActionType
  | ViewSessionActionType

type BacktestReportCardProps = {
  backtest: any
  condensed?: boolean
  isActive?: boolean
  footerActions?: FooterActions[]
  chartHeight?: number
  hideGrade?: boolean
}

const MAX_GRAPHED_RATIOS = 5
const DRAWDOWN_STRIP_HEIGHT = 44
const RESULTS_SECTION_HEIGHT = 64

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 600,
  },
})

function formatDateRange(dateRange?: { startDate?: number; endDate?: number }): string | null {
  if (!dateRange?.startDate || !dateRange?.endDate) return null
  const format = (ms: number) => formatMonthYear(ms)
  return `${format(dateRange.startDate)} – ${format(dateRange.endDate)}`
}

// Selected ratio (defaults to the most profitable) plus the ratios graphed behind it.
function useSelectedTradeRatio(summaries: any[] | undefined) {
  const sortedSummaries = useMemo(() => sortSummariesByProfit(summaries || []), [summaries])
  const [pickedGroup, setPickedGroup] = useState<string | null>(null)

  const selectedSummary =
    sortedSummaries.find((s: any) => s.group === pickedGroup) ?? sortedSummaries[0]
  const selectedGroup: string | undefined = selectedSummary?.group

  const graphedGroups = useMemo(() => {
    const top = sortedSummaries.slice(0, MAX_GRAPHED_RATIOS).map((s: any) => s.group)
    return selectedGroup && !top.includes(selectedGroup) ? [...top, selectedGroup] : top
  }, [sortedSummaries, selectedGroup])

  return { sortedSummaries, selectedSummary, selectedGroup, graphedGroups, setPickedGroup }
}

function MetaChip({ label }: { label: string }) {
  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
      sx={{
        height: 20,
        flexShrink: 0,
        fontSize: 11,
        fontWeight: 500,
        color: 'text.secondary',
        borderColor: 'background.custom4',
        backgroundColor: 'background.custom3',
        '& .MuiChip-label': { px: 0.875 },
      }}
    />
  )
}

function BacktestReportCard({
  backtest,
  condensed,
  isActive,
  footerActions,
  chartHeight = 200,
  hideGrade,
}: BacktestReportCardProps) {
  const theme = useTheme()

  const { savedBacktestsMap, usermeta, setActiveTab, setBacktestToView, backtestNameOverrides } =
    useApplicationSuiteContext()

  const { sortedSummaries, selectedSummary, selectedGroup, graphedGroups, setPickedGroup } =
    useSelectedTradeRatio(backtest.summaries)
  const hasSummaries = sortedSummaries.length > 0
  const hasClosedTrades = sortedSummaries.some((summary: any) => summary.closedTradeCount)
  const isFinished = ['complete', 'archived'].includes(backtest.status)
  const showResults = !condensed && hasSummaries && Boolean(selectedSummary)
  const dateRangeLabel = formatDateRange(backtest.backtestSettings?.dateRange)

  const agentFooterComponent = footerActions?.some((a) => a.type === 'RUN_AGENT_SESSION') ? (
    <RunAgentSessionButton backtest={backtest} />
  ) : null

  const footerComponents = footerActions
    ?.filter((a) => a.type !== 'RUN_AGENT_SESSION')
    .map((action, index) => {
      if (action.type === 'SELECT') {
        return (
          <BacktestReportCardSelectFooter
            backtest={backtest}
            condensed={condensed}
            onSelected={() => {
              action.onSelect(backtest)
            }}
            key={index}
          />
        )
      } else if (action.type === 'VIEW') {
        return (
          <Tooltip title="View backtest" placement="top" key={index}>
            <IconButton
              size="small"
              aria-label="View backtest"
              onClick={() => action.onView(backtest)}
            >
              <RemoveRedEye fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      } else if (action.type === 'OPEN_SETTINGS') {
        return (
          <OpenSettingsButton
            isActive={isActive}
            onSettingsClick={() => action.onSettingsClick(!isActive)}
            key={index}
          />
        )
      } else if (action.type === 'CREATE_ALERT') {
        return <CreateAlertActionButton backtest={backtest} size="small" key={index} />
      } else if (action.type === 'FAVORITE') {
        return <FavoriteBacktestIconButton backtest={backtest} asButton key={index} />
      } else if (action.type === 'VIEW_SESSION') {
        return (
          <Button
            onClick={() => action.onViewSession()}
            key={index}
            fullWidth
            endIcon={<SmartToyOutlinedIcon />}
          >
            View Session
          </Button>
        )
      } else {
        return null
      }
    })
  const hasFooterActions = Boolean(footerComponents?.length || agentFooterComponent)
  const showOverview = showResults && isFinished

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '--Paper-overlay': 'none !important',
        backgroundColor: theme.palette.background.custom2,
        border: `${isActive ? 2 : 1}px solid ${
          isActive ? theme.palette.primary.main : theme.palette.background.custom4
        }`,
        transition: 'all 0.3s ease',
        zIndex: isActive ? 1 : 'auto',
      }}
      className={'backtest-report-card'}
    >
      <Box
        display="flex"
        flexDirection={'column'}
        gap={0.75}
        px={2}
        pt={1.5}
        pb={showResults ? 1 : 1.5}
        data-tour-id="backtest-card"
      >
        <Box display={'flex'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
          <Box
            display={'flex'}
            alignItems={'center'}
            overflow="hidden"
            gap={1}
            data-tour-id="backtest-name"
          >
            <AssessmentIcon fontSize={'small'} />
            <Typography
              variant="body1"
              color="text.primary"
              fontWeight={600}
              overflow={'hidden'}
              whiteSpace={'nowrap'}
              textOverflow={'ellipsis'}
              onClick={(e) => {
                e.stopPropagation()
                setBacktestToView(backtest)
              }}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.5,
                mx: -0.5,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              {backtestNameOverrides[backtest.id] ?? backtest.backtestSettings.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ml: 1 }}>
            {['running', 'error'].includes(backtest.status) ? (
              <BacktestStatusIcon backtest={backtest} />
            ) : !hideGrade && hasSummaries ? (
              <BacktestGradeIcon backtest={backtest} pill />
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box sx={{ overflow: 'hidden' }}>
            <Box
              display={'flex'}
              alignItems={'center'}
              gap={1}
              data-tour-id="backtest-pattern-name"
            >
              <AccountTreeIcon
                fontSize={'small'}
                sx={{
                  color: theme.palette.text.secondary,
                }}
              />
              <PatternVersionTitle
                pattern={backtest.pattern}
                variant="body2"
                color="text.secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  setActiveTab({ tab: AppTabs.PatternBuilder, options: { item: backtest.pattern } })
                }}
              />
            </Box>
          </Box>

          <Box display={'flex'} alignItems={'center'} gap={1}>
            {savedBacktestsMap[backtest.id] && (
              <FavoriteBacktestIconButton
                backtest={backtest}
                bookmarkRemoveAsChecked
                size={'small'}
                style={{ padding: 0 }}
              />
            )}
            {backtest.source === 'strategy-agent' ? (
              <Tooltip title="Created by AI Session" placement="top">
                <SmartToyOutlinedIcon fontSize={'small'} color={'primary'} />
              </Tooltip>
            ) : backtest.gcid === usermeta.gcid ? (
              <Tooltip title="You created this backtest" placement="top">
                <AssignmentInd fontSize={'small'} color={'primary'} />
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 0.75, overflow: 'hidden', flexGrow: 1, minWidth: 0 }}>
            {backtest.backtestSettings?.executionSymbol && (
              <MetaChip label={backtest.backtestSettings.executionSymbol} />
            )}
            {dateRangeLabel && <MetaChip label={dateRangeLabel} />}
          </Box>
          {showResults && selectedGroup && (
            <BacktestRatioSelect
              summaries={sortedSummaries}
              selectedGroup={selectedGroup}
              onChange={setPickedGroup}
            />
          )}
        </Box>
      </Box>

      {!condensed && (
        // Fixed body height keeps every card the same size; VirtuosoGrid assumes uniform items.
        <Box
          sx={{
            height: chartHeight + DRAWDOWN_STRIP_HEIGHT + RESULTS_SECTION_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {showResults && selectedGroup && (
            <Box
              sx={{
                height: RESULTS_SECTION_HEIGHT,
                flexShrink: 0,
                px: 2,
                pb: 1,
              }}
            >
              <BacktestStatStrip stats={getTradeRatioStats(selectedSummary)} />
            </Box>
          )}

          <CardContent
            sx={{
              position: 'relative',
              minHeight: 0,
              flexGrow: 1,
              p: 0,
              '&:last-child': { pb: 0 },
            }}
          >
            {!hasSummaries && (
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                height={'100%'}
                flexDirection={'column'}
                px={2}
              >
                <Typography variant={'body1'} color={'textPrimary'}>
                  No trades were executed during this backtest
                </Typography>
                <Typography variant={'caption'} color={'textSecondary'} textAlign={'center'}>
                  Consider refining your strategy parameters or adjusting the timeframe to generate
                  more meaningful results.
                </Typography>
              </Box>
            )}
            {hasSummaries && hasClosedTrades && (
              <BacktestEquityChart
                backtestId={backtest.id}
                tradeGroups={graphedGroups}
                accountEquity={backtest.backtestSettings.analyzeTrades.equity}
                highlightGroup={selectedGroup}
              />
            )}
          </CardContent>
        </Box>
      )}
      <CardActions
        sx={{
          position: 'relative',
          minHeight: condensed ? 30 : hasFooterActions || showOverview ? 48 : 0,
          background: theme.palette.background.custom3,
          p: 0,
          pl: showOverview ? 2 : 1,
          pr: 1,
          py: hasFooterActions || showOverview ? 0.5 : 0,
          borderTop:
            condensed || !(hasFooterActions || showOverview)
              ? 'none'
              : `1px solid ${theme.palette.background.custom4}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {isFinished ? (
          <>
            {showOverview && (
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <BacktestRatiosOverview summaries={sortedSummaries} />
              </Box>
            )}
            <Box
              sx={{
                flex: showOverview ? '0 0 auto' : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.25,
                minWidth: 0,
              }}
            >
              {footerComponents}
              {agentFooterComponent}
            </Box>
          </>
        ) : (
          <>
            {backtest.status === 'running' && (
              <Typography
                variant={'caption'}
                color={'textSecondary'}
                width={'100%'}
                textAlign={'center'}
              >
                View backtest results when complete
              </Typography>
            )}
          </>
        )}
      </CardActions>
    </Card>
  )
}

function OpenSettingsButton({ isActive, onSettingsClick }: any) {
  const label = isActive ? 'Close settings' : 'Open settings'

  return (
    <Tooltip title={label} placement="top">
      <IconButton
        size="small"
        aria-label={label}
        onClick={onSettingsClick}
        color={isActive ? 'error' : 'primary'}
        data-tour-id="open-settings-button"
      >
        {isActive ? <Close fontSize="small" /> : <Settings fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

function BacktestReportCardSelectFooter({ backtest, condensed, onSelected, isSelected }: any) {
  const { usermeta } = useApplicationSuiteContext()

  const [isHovering, setIsHovering] = useState(false)

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={'100%'}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Box display={'flex'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
        {usermeta.isAdmin && isHovering && (
          <CustomWidthTooltip
            arrow={true}
            placement={'top-start'}
            title={
              <React.Fragment>
                <Box>
                  <Box borderBottom={1}>
                    <Typography variant="body2" color={'textPrimary'}>
                      Admin Only
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      display={'grid'}
                      gridTemplateRows={'1fr 1fr 1fr'}
                      gridTemplateColumns={'1fr 5fr'}
                      alignItems={'center'}
                    >
                      <Typography variant="body2" color={'textSecondary'}>
                        Backtest:
                      </Typography>
                      <Typography variant={'body1'} mx={'auto'} color={'textPrimary'}>
                        {backtest.id}
                      </Typography>
                      <Typography variant="body2" color={'textSecondary'}>
                        Pattern:
                      </Typography>
                      <Typography variant={'body1'} mx={'auto'} color={'textPrimary'}>
                        {backtest.pattern?.id}
                      </Typography>
                      <Typography variant="body2" color={'textSecondary'}>
                        Runner:
                      </Typography>
                      <Typography variant={'body1'} mx={'auto'} color={'textPrimary'}>
                        {backtest.gcid}
                      </Typography>
                      <Typography variant="body2" color={'textSecondary'}>
                        Owner:
                      </Typography>
                      <Typography variant={'body1'} mx={'auto'} color={'textPrimary'}>
                        {backtest.backtestSettings?.gcid}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            }
          >
            <IconButton size={'small'} onClick={() => console.log(backtest)}>
              <AdminPanelSettings color={'warning'} fontSize={'small'} />
            </IconButton>
          </CustomWidthTooltip>
        )}
        <ActionChip
          sx={{ ml: 'auto', width: 'auto' }}
          variant={isSelected ? 'filled' : 'outlined'}
          label={'Select'}
          size={condensed ? 'small' : 'medium'}
          onClick={() => onSelected(backtest)}
          forceOpen={true}
        />
      </Box>
    </Box>
  )
}

export default BacktestReportCard
